import React, { useState, useEffect } from 'react';
import { Modal, Spin, Typography, Button, Space, Alert, List, Badge, Avatar, Divider } from 'antd';
import { QrcodeOutlined, ReloadOutlined, CheckCircleOutlined, MessageOutlined, UserOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface Chat {
  id: string;
  name: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  isMuted: boolean;
  isPinned: boolean;
  isRead: boolean;
}

interface WhatsAppQRScannerProps {
  visible: boolean;
  onClose: () => void;
  onAuthenticated?: () => void;
  apiBaseUrl?: string;
  profileId?: string;
}

export const WhatsAppQRScanner: React.FC<WhatsAppQRScannerProps> = ({
  visible,
  onClose,
  onAuthenticated,
  apiBaseUrl = 'http://localhost:3001',
  profileId = '1'
}) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusCheckInterval, setStatusCheckInterval] = useState<NodeJS.Timeout | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [chatListInterval, setChatListInterval] = useState<NodeJS.Timeout | null>(null);

  // Fetch QR code
  const fetchQRCode = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/qrcode?profileId=${profileId}`);
      const data = await response.json();
      
      if (data.success) {
        if (data.authenticated) {
          setAuthenticated(true);
          setQrCode(null);
          if (onAuthenticated) {
            onAuthenticated();
          }
        } else if (data.qrCode) {
          setQrCode(data.qrCode);
          setAuthenticated(false);
        }
      } else {
        setError(data.message || 'Failed to fetch QR code');
      }
    } catch (err) {
      setError('Failed to connect to QR scanner service. Make sure the service is running.');
      console.error('Error fetching QR code:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize WhatsApp Web
  const initializeWhatsApp = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/init?profileId=${profileId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (data.authenticated) {
          setAuthenticated(true);
          setQrCode(null);
          if (onAuthenticated) {
            onAuthenticated();
          }
        } else if (data.qrCode) {
          setQrCode(data.qrCode);
          setAuthenticated(false);
        }
      } else {
        setError(data.message || 'Failed to initialize WhatsApp Web');
      }
    } catch (err) {
      setError('Failed to connect to QR scanner service. Make sure the service is running.');
      console.error('Error initializing WhatsApp:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check authentication status
  const checkStatus = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/status?profileId=${profileId}`);
      const data = await response.json();
      console.log('data', data);
      if (data.success && data.authenticated) {
        setAuthenticated(true);
        setQrCode(null);
        if (onAuthenticated) {
          onAuthenticated();
        }
        // Stop polling once authenticated
        if (statusCheckInterval) {
          clearInterval(statusCheckInterval);
          setStatusCheckInterval(null);
        }
        // Start fetching chat list
        fetchChatList(true);
      }
    } catch (err) {
      console.error('Error checking status:', err);
    }
  };

  // Fetch chat list
  const fetchChatList = async (isAuthenticated?: boolean) => {
    console.log('fetchChatList', isAuthenticated);
    if(!isAuthenticated) return;
    //else if (!authenticated) return;
    console.log('fetchChatList authenticated', authenticated);
    setChatsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/chats?profileId=${profileId}`);
      const data = await response.json();
      
      if (data.success && data.chats) {
        setChats(data.chats);
      }
    } catch (err) {
      console.error('Error fetching chat list:', err);
    } finally {
      setChatsLoading(false);
    }
  };

  // Initialize when modal opens
  useEffect(() => {
    if (visible) {
      initializeWhatsApp();
      
      // Start polling for authentication status every 2 seconds
      const interval = setInterval(checkStatus, 2000);
      setStatusCheckInterval(interval);
      
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    } else {
      // Clean up when modal closes
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
      if (chatListInterval) {
        clearInterval(chatListInterval);
        setChatListInterval(null);
      }
      setQrCode(null);
      setAuthenticated(false);
      setError(null);
      setChats([]);
    }
  }, [visible]);

  // Poll chat list when authenticated
  useEffect(() => {
    if (authenticated && visible) {
      // Fetch immediately
      fetchChatList();
      
      // Then poll every 5 seconds
      const interval = setInterval(fetchChatList, 5000);
      setChatListInterval(interval);
      
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [authenticated, visible]);

  // Refresh QR code
  const handleRefresh = () => {
    fetchQRCode();
  };

  return (
    <Modal
      title={
        <Space>
          <QrcodeOutlined />
          <span>Scan WhatsApp QR Code</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        authenticated && (
          <Button key="refresh-chats" icon={<ReloadOutlined />} onClick={() => fetchChatList(false)} loading={chatsLoading}>
            Refresh Chats
          </Button>
        ),
        !authenticated && (
          <Button key="refresh" icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh QR Code
          </Button>
        ),
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={authenticated ? 600 : 500}
      centered
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        {loading && !qrCode && !authenticated && (
          <div style={{ padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text>Initializing WhatsApp Web...</Text>
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '20px' }}
            action={
              <Button size="small" onClick={initializeWhatsApp}>
                Retry
              </Button>
            }
          />
        )}

        {authenticated && (
          <div>
            <div style={{ marginBottom: '16px', textAlign: 'center' }}>
              <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '8px' }} />
              <Title level={5} style={{ color: '#52c41a', margin: 0 }}>
                Successfully Authenticated!
              </Title>
            </div>
            <Divider />
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5} style={{ margin: 0 }}>
                  <MessageOutlined style={{ marginRight: '8px' }} />
                  Chats ({chats.length})
                </Title>
                {chatsLoading && <Spin size="small" />}
              </div>
              {chats.length === 0 && !chatsLoading && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  <Text type="secondary">No chats found</Text>
                </div>
              )}
              {chats.length > 0 && (
                <List
                  dataSource={chats}
                  loading={chatsLoading}
                  renderItem={(chat: Chat) => (
                    <List.Item
                      style={{
                        cursor: 'pointer',
                        padding: '12px',
                        borderRadius: '8px',
                        marginBottom: '4px',
                        backgroundColor: chat.unreadCount > 0 ? '#f0f7ff' : 'transparent',
                        border: chat.unreadCount > 0 ? '1px solid #d4edda' : '1px solid transparent'
                      }}
                    >
                      <List.Item.Meta
                        avatar={
                          <Badge count={chat.unreadCount > 0 ? chat.unreadCount : 0} size="small" offset={[-5, 5]}>
                            <Avatar icon={<UserOutlined />} />
                          </Badge>
                        }
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong style={{ fontSize: '14px' }}>
                              {chat.name}
                              {chat.isPinned && ' 📌'}
                              {chat.isMuted && ' 🔇'}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              {chat.lastMessageTime}
                            </Text>
                          </div>
                        }
                        description={
                          <div>
                            <Text
                              ellipsis
                              style={{
                                color: chat.unreadCount > 0 ? '#000' : '#666',
                                fontWeight: chat.unreadCount > 0 ? 500 : 400,
                                fontSize: '13px'
                              }}
                            >
                              {chat.lastMessage || 'No messages yet'}
                            </Text>
                            {chat.unreadCount > 0 && (
                              <Badge
                                count={chat.unreadCount}
                                style={{
                                  marginLeft: '8px',
                                  backgroundColor: '#1890ff'
                                }}
                              />
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </div>
          </div>
        )}

        {qrCode && !authenticated && (
          <div>
            <Title level={5} style={{ marginBottom: '16px' }}>
              Scan this QR code with your phone
            </Title>
            <div style={{ 
              display: 'inline-block', 
              padding: '16px', 
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #f0f0f0'
            }}>
              <img 
                src={qrCode} 
                alt="WhatsApp QR Code" 
                style={{ 
                  maxWidth: '100%', 
                  height: 'auto',
                  display: 'block'
                }} 
              />
            </div>
            <div style={{ marginTop: '16px' }}>
              <Text type="secondary">
                1. Open WhatsApp on your phone<br />
                2. Tap Menu or Settings and select Linked Devices<br />
                3. Tap on Link a Device<br />
                4. Point your phone at this screen to capture the code
              </Text>
            </div>
            {loading && (
              <div style={{ marginTop: '16px' }}>
                <Spin size="small" />
                <Text type="secondary" style={{ marginLeft: '8px' }}>
                  Checking authentication status...
                </Text>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

